import { format, parseISO, differenceInDays } from 'date-fns'
import { vi, enUS, zhCN, ko, ru } from 'date-fns/locale'
import type { DateRange } from 'react-day-picker'

const locales = {
  vi,
  en: enUS,
  zh: zhCN,
  ko,
  ru,
}

function getWeekdayAbbr(date: Date, locale: string = 'vi'): string {
  return format(date, 'EEEE', { locale: locales[locale] })
}

function getWeekdayFull(date: Date, locale: string = 'vi'): string {
  return format(date, 'EEEE', { locale: locales[locale] })
}

export const formatDate = (date: string | Date, formatStr = 'PP', locale = 'vi') => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return format(dateObj, formatStr, {
    locale: locales[locale as keyof typeof locales],
  })
}

export function formatSingleDateShort(
  date: Date | string | undefined,
  locale: string = 'vi',
  t: (key: string) => string
): string {
  if (!date) return t('common.selectDate')

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const weekday = getWeekdayAbbrShort(dateObj, locale)
  const formattedDate = format(dateObj, 'dd/MM/yyyy', { locale: locales[locale] })

  return `${weekday}, ${formattedDate}`
}
export function getWeekdayAbbrShort(date: Date, locale: string = 'vi'): string {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })
  const raw = formatter.format(date)

  switch (locale) {
    case 'vi':
      // Handle both "Th 2" and "Thứ 2" (with or without diacritic), optional space
      return raw.replace(/^Th(ứ)?\s*/i, 'T') // "Th 2" or "Thứ 2" -> "T2"
    case 'zh':
      return raw.replace('星期', '周') // "星期一" -> "周一"
    default:
      return raw // "Mon" (en), "월" (ko), etc.
  }
}
export function formatAbbrDateShort(
  date: Date | string | undefined,
  locale: string = 'vi',
  t: (key: string) => string
): string {
  if (!date) return t('common.selectDate')

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const weekday = getWeekdayAbbrShort(dateObj, locale)
  const usedLocale = locales[locale] || locales['vi']
  const formattedDate = format(dateObj, 'dd/MM/yyyy', { locale: usedLocale })

  return `${weekday}, ${formattedDate}`
}
export function formatDateToISO(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function formatDateRangeShort(
  dateRange: DateRange | undefined,
  locale: string = 'vi',
  t: (key: string) => string
): string {
  if (!dateRange?.from) return t('common.selectDate')
  if (!dateRange?.to) return format(dateRange.from, 'EEEE, dd/MM/yyyy', { locale: locales[locale] })

  const dayFrom = getWeekdayAbbrShort(dateRange.from, locale)
  const dayTo = getWeekdayAbbrShort(dateRange.to, locale)

  const dateFrom = format(dateRange.from, 'dd/MM/yyyy', { locale: locales[locale] })
  const dateTo = format(dateRange.to, 'dd/MM/yyyy', { locale: locales[locale] })

  return `${dayFrom}, ${dateFrom} - ${dayTo}, ${dateTo}`
}
export function formatDateShort(
  date: Date | undefined,
  locale: string = 'vi',
  t: (key: string) => string
): string {
  if (!date) return t('common.selectDate')

  const dayFrom = getWeekdayAbbrShort(date, locale)

  const dateFrom = format(date, 'dd/MM/yyyy', { locale: locales[locale] })

  return `${dayFrom}, ${dateFrom}`
}
export function formatDateRangeFull(
  dateRange: DateRange | undefined,
  locale: string = 'vi',
  t: (key: string) => string
): string {
  if (!dateRange?.from) return t('common.selectDate')
  if (!dateRange?.to)
    return format(dateRange.from, 'EEEE, dd MMMM yyyy', { locale: locales[locale] })

  const dayFrom = getWeekdayAbbrShort(dateRange.from, locale)
  const dayTo = getWeekdayAbbrShort(dateRange.to, locale)

  const dateFrom = format(dateRange.from, 'dd MMMM yyyy', { locale: locales[locale] })
  const dateTo = format(dateRange.to, 'dd MMMM yyyy', { locale: locales[locale] })

  return `${dayFrom}, ${dateFrom} - ${dayTo}, ${dateTo}`
}

export const formatDateTime = (date: string | Date, locale = 'vi') => {
  return formatDate(date, 'PPpp', locale)
}

export const formatShortDate = (date: string | Date, locale = 'vi') => {
  return formatDate(date, 'P', locale)
}

export const formatShortDateTime = (date: string | Date, locale = 'vi') => {
  return formatDate(date, 'Pp', locale)
}

export const calculateDaysBetweenDates = (startDate: Date, endDate: Date) => {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

export const addDays = (date: Date, days: number) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export const isDateInRange = (date: Date, startDate: Date, endDate: Date) => {
  return date >= startDate && date <= endDate
}

export const getWeekDays = (locale = 'vi') => {
  const baseDate = new Date(2023, 0, 1) // Sunday
  const weekDays: string[] = []

  for (let i = 0; i < 7; i++) {
    weekDays.push(getWeekdayAbbr(addDays(baseDate, i), locale))
  }

  return weekDays
}

export const getMonthDays = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate()
}

export function calculateDurationOfStay(checkIn: string | Date, checkOut: string | Date): number {
  const checkInDate = typeof checkIn === 'string' ? new Date(checkIn) : checkIn
  const checkOutDate = typeof checkOut === 'string' ? new Date(checkOut) : checkOut
  return differenceInDays(checkOutDate, checkInDate)
}

export const formatSingleDate = (date: string | Date): string => {
  if (!date) return ''
  return formatDate(date, 'dd/MM/yyyy')
}

/**
 * Kiểm tra xem một ngày có thực sự tồn tại hay không
 * Ví dụ: 2024-02-29 tồn tại (năm nhuận), nhưng 2025-02-29 không tồn tại
 */
export const isValidDate = (date: Date | string | number): boolean => {
  // Chuyển đổi input thành Date object
  const d = new Date(date)

  // Kiểm tra nếu không phải date hợp lệ
  if (isNaN(d.getTime())) {
    return false
  }

  // Lấy các thành phần của ngày từ input
  const year = d.getFullYear()
  const month = d.getMonth() // 0-11
  const day = d.getDate()

  // Tạo một date mới với các thành phần đó
  const newDate = new Date(year, month, day)

  // So sánh các thành phần của ngày mới với ngày gốc
  // Nếu khác nhau tức là ngày đã bị JavaScript tự động điều chỉnh
  return newDate.getFullYear() === year && newDate.getMonth() === month && newDate.getDate() === day
}
